import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { User, UserProfile, PermissionKey } from "@/types/permissions";
import { mockUsers, mockUserProfiles } from "@/data/mockUserProfiles";

interface PermissionsContextType {
  currentUser: User | null;
  currentProfile: UserProfile | null;
  hasPermission: (permission: PermissionKey) => boolean;
  hasAnyPermission: (permissions: PermissionKey[]) => boolean;
  hasAllPermissions: (permissions: PermissionKey[]) => boolean;
  setCurrentUser: (user: User | null) => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentProfile, setCurrentProfile] = useState<UserProfile | null>(null);

  // Initialize with first user (admin) for demo purposes
  useEffect(() => {
    const defaultUser = mockUsers[0]; // Admin user
    setCurrentUser(defaultUser);
  }, []);

  // Update profile when user changes
  useEffect(() => {
    if (currentUser) {
      const profile = mockUserProfiles.find((p) => p.id === currentUser.profileId);
      setCurrentProfile(profile || null);
    } else {
      setCurrentProfile(null);
    }
  }, [currentUser]);

  const hasPermission = (permission: PermissionKey): boolean => {
    if (!currentProfile) return false;
    return currentProfile.permissions.includes(permission);
  };

  const hasAnyPermission = (permissions: PermissionKey[]): boolean => {
    if (!currentProfile) return false;
    return permissions.some((permission) => currentProfile.permissions.includes(permission));
  };

  const hasAllPermissions = (permissions: PermissionKey[]): boolean => {
    if (!currentProfile) return false;
    return permissions.every((permission) => currentProfile.permissions.includes(permission));
  };

  return (
    <PermissionsContext.Provider
      value={{
        currentUser,
        currentProfile,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        setCurrentUser,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionsProvider");
  }
  return context;
}
